<template lang="pug">
  div(v-if="contactId")
    chat-history(
      ref="chatHistory"
      :send-message-callback="sendMessageCallback"
      :send-button-disabled="sendingTextInProgress"
      :initial-contact-info="conversation"
      :is-loading="conversationsInProgress"
      :current-user="user"
      :phone-number="phoneNumber"
      @load-more-callback="increaseChatHistoryPagination"
      @compose-new-text="composeNewText"
      v-model="chatHistory"
      is-contact-chat
    )
</template>
<script>
import { mapState, createNamespacedHelpers } from 'vuex';
import ChatHistory from '@/Text/components/ChatHistory';
import { EventBus } from '@/event-bus';
import { TypeFilters } from '@/Contacts/config/scheduleds-filters';

const {
  mapActions: mapConversationsActions,
  mapState: mapConversationsState,
  mapMutations: mapConversationsMutations,
} = createNamespacedHelpers('conversations');

const { mapActions: mapContactActions } =
  createNamespacedHelpers('contact');

const { mapGetters: mapUserInformationGetters } = createNamespacedHelpers('userInformation');

const optInOptions = {
  ORIGINAL_SMS: 'should send original sms',
  SMS_AND_OPT_IN: 'should send opt in',
};

export default {
  components: {
    ChatHistory,
  },
  props: {
    contactId: Number,
    resetChat: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chatHistoryPagination: 0,
      conversation: {},
    };
  },
  computed: {
    ...mapState({
      user: state => state.userInformation.user,
    }),
    ...mapState({
      contact: state => state.contact.contactChat,
    }),
    ...mapConversationsState([
      'chatHistory',
      'conversations',
      'sendingTextInProgress',
      'conversationsInProgress',
    ]),
    phoneNumber() {
      return this.contact && this.contact.mobile_phone ? this.contact.mobile_phone : null;
    },
  },
  watch: {
    conversation: {
      async handler() {
        this.resetChatHistory();
      },
      deep: true,
    },
    contactId: {
      async handler() {
        if (!this.contactId) return;
        this.resetChatHistory();
        await this.findContact({ contactId: this.contactId, isChat: true });
        this.conversation = {
          user_id: this.user.id,
          id: null,
          contact: {
            id: this.contact.id,
            mobile_phone: this.contact.mobile_phone,
            first_name: this.contact.first_name,
            last_name: this.contact.last_name,
            assigned_user_id: this.contact.assigned_user_id,
          },
        };
        await this.getConversation();
      },
      immediate: true,
    },
    async resetChat(value) {
      if (value) {
        this.resetChatHistory();
        await this.getConversation();
        this.$emit('reset-completed');
      }
    },
  },
  methods: {
    ...mapConversationsActions([
      'sendMessages',
      'setSelectedContactId',
      'getChatHistory',
      'clearChatHistory',
    ]),
    ...mapContactActions(['findContact']),
    ...mapConversationsMutations(['resetMessage']),
    ...mapUserInformationGetters(['userHasFeatureFlag']),
    /**
     * Resets the chat history to the given conversation
     *
     * @param {Array} conversation The array of texts between a user and a contact
     */
    resetChatHistory() {
      this.clearChatHistory();
      this.resetMessage();
      this.chatHistoryPagination = 0;
    },
    /**
     * Sends the new text and resets the chat history when it finishes, either
     * on error or on success.
     */
    async sendMessageCallback(messagePayload) {
      let contact = {
        id: this.contact.id,
        mobile_phone: this.contact.mobile_phone,
      };

      const { optInSelection } = messagePayload.sendOptions;
      if (optInSelection === optInOptions.ORIGINAL_SMS) {
        contact.should_send_opt_in = false;
        contact.should_send_original_sms = true;
      }
      if (optInSelection === optInOptions.SMS_AND_OPT_IN) {
        contact.should_send_opt_in = true;
        contact.should_send_original_sms = true;
      }

      const payload =
        !this.contactConversation || !this.contactConversation.id
          ? {
              body: messagePayload.body,
              userTimezone: messagePayload.userTimezone,
              imagesURLs: messagePayload.imagesURLs,
              sendOptions: messagePayload.sendOptions,
              contacts: [contact],
              messagePayload,
            }
          : messagePayload;

      await this.sendMessages(payload);
      this.$emit('on-sms-sent');

      const { sendOptions } = messagePayload;
      const scheduleAction = (sendOptions.schedule && sendOptions.schedule.action) || '';

      if (scheduleAction === 'send_sms_scheduled') {
        EventBus.$emit('scheduled-communication', TypeFilters.Sms);
      }

      await this.getConversation();
    },
    callChatHistory(sendOptions) {
      this.$refs.chatHistory.sendMessage(sendOptions);
    },
    /**
     * Obtain new data with desired pagination, to be appended to the list of chat history
     */
    async increaseChatHistoryPagination() {
      await this.getChatHistory({
        offset: this.chatHistoryPagination,
        conversation: this.conversation,
      });
      if (this.chatHistory.length) {
        // If there are new chat history, increase the pagination
        this.chatHistoryPagination += this.chatHistory.length;
      }
    },
    /**
     * Redirects to sending bulk text page
     */
    composeNewText() {
      this.$router.push('/communications/text/bulk');
    },
    async getConversation() {
      await this.getChatHistory({
        offset: this.chatHistoryPagination,
        conversation: this.conversation,
      });
      this.conversation.last_text = this.chatHistory[this.chatHistory.length - 1];
    },
  },
};
</script>
